import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import Grid from '@material-ui/core/Grid';
import {
    InputLabel,
    Tooltip,
    IconButton,
    Checkbox,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    withStyles
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { SecondaryButton, TextBox, ColorPicker, SecondaryCheckbox } from '../../../Core/FormInput';
import AppContext from '../../../App/AppContext';
import { getInspectionSheetListById, addOrUpdateInspectionSheetListOptions } from '../../../Core/Service/inspectionSheetList-service';

const StyledHeaderCell = withStyles(() => ({
    root: {
        backgroundColor: '#1e293b',
        color: '#ffffff',
        fontWeight: 600,
        fontSize: '13px',
        padding: '10px 16px',
        borderBottom: '1px solid #334155'
    }
}))(TableCell);

const StyledBodyCell = withStyles(() => ({
    root: {
        fontSize: '13px',
        padding: '8px 16px',
        borderBottom: '1px solid #f1f5f9'
    }
}))(TableCell);

const InspectionSheetListOption = (props) => {
    const { showToast } = useContext(AppContext);

    const [state, setState] = useState({
        listName: '',
        rows: [],
        listOptionName: '',
        backgroundColour: '#53AF50',
        foregroundColour: '#FFFFFF',
        inspectionSheetListOptionSetFailed: false,
        isUpdate: false,
        optionID: null,
        listOptionId: null,
        showLoader: true,
        btnLoader: false
    });

    const Id = props.listId;
    const optionNameRef = useRef(null);

    // Keep the caret in Option Name so options can be entered one after another.
    // Deferred a tick because the dialog transition steals focus on open.
    const focusOptionName = useCallback(() => {
        setTimeout(() => optionNameRef.current && optionNameRef.current.focus(), 0);
    }, []);

    useEffect(() => {
        if (Id) {
            pullOptionsAndUpdateState();
        }
        focusOptionName();
    }, [Id]);

    const pullOptionsAndUpdateState = async () => {
        let result = await getInspectionSheetListById(Id);
        if (result && result.success && result.data) {
            setState((st) => ({
                ...st,
                listName: result.data.listName,
                showLoader: false,
                rows: (result.data.listOptions || []).map((opt, idx) => ({
                    ...opt,
                    id: opt.listOptionId || idx + 1,
                    MarkForDelete: false
                }))
            }));
        } else {
            setState((st) => ({ ...st, showLoader: false }));
            showToast((result && result.message) || 'Failed to fetch options');
        }
    };

    const resetForm = () => {
        setState((st) => ({
            ...st,
            listOptionName: '',
            backgroundColour: '#53AF50',
            foregroundColour: '#FFFFFF',
            inspectionSheetListOptionSetFailed: false,
            isUpdate: false,
            optionID: null,
            listOptionId: null
        }));
    };

    const handleEditOption = useCallback(
        (row) => {
            setState((st) => ({
                ...st,
                listOptionId: row.listOptionId,
                listOptionName: row.listOptionName || '',
                backgroundColour: row.backgroundColour || '#53AF50',
                foregroundColour: row.foregroundColour || '#FFFFFF',
                inspectionSheetListOptionSetFailed: row.inspectionSheetListOptionSetFailed || false,
                isUpdate: true,
                optionID: row.id || row.listOptionId
            }));
            focusOptionName();
        },
        [focusOptionName]
    );

    const handleDeleteOption = useCallback(
        async (row) => {
            if (!row.listOptionId) {
                return;
            }

            const optionPayload = {
                listOptionId: row.listOptionId,
                listOptionName: row.listOptionName,
                MarkForDelete: true,
                backgroundColour: row.backgroundColour,
                foregroundColour: row.foregroundColour,
                inspectionSheetListOptionSetFailed: row.inspectionSheetListOptionSetFailed || false
            };

            setState((st) => ({ ...st, showLoader: true }));
            let res = await addOrUpdateInspectionSheetListOptions(Id, [optionPayload]);
            if (res && res.success) {
                showToast(res.message || 'Option deleted successfully.');
                setState((st) => {
                    const isDeletingCurrentEdit = st.optionID === row.id;
                    if (isDeletingCurrentEdit) {
                        return {
                            ...st,
                            listOptionName: '',
                            backgroundColour: '#53AF50',
                            foregroundColour: '#FFFFFF',
                            inspectionSheetListOptionSetFailed: false,
                            isUpdate: false,
                            optionID: null,
                            listOptionId: null
                        };
                    }
                    return st;
                });
                pullOptionsAndUpdateState();
            } else {
                setState((st) => ({ ...st, showLoader: false }));
                showToast((res && res.message) || 'Failed to delete option.');
            }
        },
        [Id, showToast]
    );

    const addOptionLocally = async () => {
        if (!state.listOptionName || !state.listOptionName.trim()) {
            showToast('Option Name is required');
            return;
        }

        const duplicate = state.rows.find(
            (item) =>
                !item.MarkForDelete &&
                item.listOptionName &&
                item.listOptionName.toLowerCase().trim() === state.listOptionName.toLowerCase().trim() &&
                item.id !== state.optionID
        );
        if (duplicate) {
            showToast('Two Options cannot have the same value.');
            return;
        }

        const optionPayload = {
            listOptionId: state.listOptionId || 0,
            listOptionName: state.listOptionName,
            MarkForDelete: false,
            backgroundColour: state.backgroundColour,
            foregroundColour: state.foregroundColour,
            inspectionSheetListOptionSetFailed: state.inspectionSheetListOptionSetFailed
        };

        setState((st) => ({ ...st, btnLoader: true }));
        let res = await addOrUpdateInspectionSheetListOptions(Id, [optionPayload]);
        setState((st) => ({ ...st, btnLoader: false }));
        if (res && res.success) {
            showToast(res.message || (state.isUpdate ? 'Option updated successfully.' : 'Option added successfully.'));
            resetForm();
            pullOptionsAndUpdateState();
            focusOptionName();
        } else {
            showToast((res && res.message) || 'Failed to save option.');
        }
    };

    const inputChange = (e) => {
        const { name, value } = e.target;
        setState((st) => ({ ...st, [name]: value }));
    };

    const handleCheckbox = (e) => {
        const { name, checked } = e.target;
        setState((st) => ({ ...st, [name]: checked }));
    };

    const onColorChange = (updatedColor, name) => {
        if (updatedColor) {
            setState((st) => ({ ...st, [name]: updatedColor.hex }));
        }
    };

    const filteredRows = useMemo(() => state.rows.filter((item) => !item.MarkForDelete), [state.rows]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '10px 4px', minHeight: '620px' }}>
            <Grid container spacing={2} alignItems="center">
                {/* Option Name */}
                <Grid item xs={12} sm md={4}>
                    <InputLabel shrink required style={{ fontSize: '13px', fontWeight: 600, marginBottom: '0px' }}>
                        Option Name
                    </InputLabel>
                    <TextBox
                        placeholder="Option Name"
                        value={state.listOptionName}
                        onChange={inputChange}
                        name="listOptionName"
                        autoFocus
                        inputRef={optionNameRef}
                    />
                </Grid>

                {/* Background Color Picker */}
                <Grid item xs={6} sm md>
                    <InputLabel shrink style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                        Background Colour
                    </InputLabel>
                    <ColorPicker height="26px" color={state.backgroundColour} onChange={(m) => onColorChange(m, 'backgroundColour')} />
                </Grid>

                {/* Text Color Picker */}
                <Grid item xs={6} sm md>
                    <InputLabel shrink style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                        Text Colour
                    </InputLabel>
                    <ColorPicker height="26px" color={state.foregroundColour} onChange={(m) => onColorChange(m, 'foregroundColour')} />
                </Grid>

                {/* Set Failed Checkbox */}
                <Grid item xs={6} sm md>
                    <InputLabel shrink style={{ visibility: 'hidden', marginBottom: '-10px' }}>
                        Failed
                    </InputLabel>
                    <div style={{ height: '38px', alignItems: 'center', justifyContent: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={state.inspectionSheetListOptionSetFailed}
                                    name="inspectionSheetListOptionSetFailed"
                                    onChange={handleCheckbox}
                                    color="secondary"
                                />
                            }
                            label={<span style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>Set Failed?</span>}
                        />
                    </div>
                </Grid>

                {/* Add / Update Button */}
                <Grid item xs={6} sm md>
                    <InputLabel shrink style={{ visibility: 'hidden', marginBottom: '6px' }}>
                        Action
                    </InputLabel>
                    <SecondaryButton
                        onClick={addOptionLocally}
                        fullWidth
                        className="submit_btn"
                        isBtnLoader={state.btnLoader}
                        style={{ height: '38px', fontWeight: 700 }}
                    >
                        {state.isUpdate ? 'Update' : 'Add'}
                    </SecondaryButton>
                </Grid>
            </Grid>

            {/* Table */}
            <TableContainer
                component={Paper}
                style={{
                    height: '470px',
                    maxHeight: '470px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    boxShadow: 'none',
                    overflowY: 'auto'
                }}
            >
                <Table stickyHeader size="small" aria-label="inspection sheet list options table">
                    <TableHead>
                        <TableRow>
                            <StyledHeaderCell align="center" style={{ width: '100px' }}>
                                Action
                            </StyledHeaderCell>
                            <StyledHeaderCell align="left">Name</StyledHeaderCell>
                            <StyledHeaderCell align="center" style={{ width: '130px' }}>
                                Set Failed
                            </StyledHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {state.showLoader ? (
                            <TableRow>
                                <StyledBodyCell colSpan={3} align="center" style={{ padding: '30px' }}>
                                    <CircularProgress size={24} />
                                </StyledBodyCell>
                            </TableRow>
                        ) : filteredRows.length === 0 ? (
                            <TableRow>
                                <StyledBodyCell colSpan={3} align="center" style={{ padding: '24px', color: '#94a3b8', fontSize: '13px' }}>
                                    No options found
                                </StyledBodyCell>
                            </TableRow>
                        ) : (
                            filteredRows.map((row) => (
                                <TableRow key={row.id || row.listOptionId} hover>
                                    <StyledBodyCell align="center">
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center' }}>
                                            <Tooltip title="Edit" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditOption(row)}
                                                    style={{ padding: '4px', backgroundColor: '#f1f5f9', borderRadius: '4px' }}
                                                >
                                                    <EditIcon fontSize="small" color="secondary" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteOption(row)}
                                                    style={{ padding: '4px', backgroundColor: '#fee2e2', borderRadius: '4px' }}
                                                >
                                                    <DeleteIcon fontSize="small" style={{ color: '#dc2626' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    </StyledBodyCell>
                                    <StyledBodyCell align="left">
                                        <div
                                            style={{
                                                backgroundColor: row.backgroundColour || '#53AF50',
                                                color: row.foregroundColour || '#FFFFFF',
                                                padding: '4px 14px',
                                                fontWeight: '600',
                                                fontSize: '13px',
                                                borderRadius: '4px',
                                                display: 'inline-block',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                                            }}
                                        >
                                            {row.listOptionName}
                                        </div>
                                    </StyledBodyCell>
                                    <StyledBodyCell align="center">
                                        {row.inspectionSheetListOptionSetFailed ? (
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    padding: '2px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fee2e2',
                                                    color: '#dc2626',
                                                    border: '1px solid #fecaca'
                                                }}
                                            >
                                                Yes
                                            </span>
                                        ) : (
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    padding: '2px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    backgroundColor: '#f1f5f9',
                                                    color: '#64748b',
                                                    border: '1px solid #e2e8f0'
                                                }}
                                            >
                                                No
                                            </span>
                                        )}
                                    </StyledBodyCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default InspectionSheetListOption;
